import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { isReducedMotion } from '@atlaskit/motion';
import type { MinimizableModalProps, MinimizableModalView } from '../../types.tsx';
import { TransitionState, type Context, type TransitionStateEnum } from './types.tsx';
import { usePrevious } from './utils.tsx';

const MinimizableModalContext = createContext<Context>({
	shouldFocusChanger: false,
	view: 'modal',
	previousView: 'modal',
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setView: () => {},
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	unminimizeModal: () => {},
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onClose: () => {},
	transitionByKeyboardFrom: null,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setTransitionByKeyboardFrom: () => {},
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setShouldFocusChanger: () => {},
	isTransitionInState: () => false,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setTransitionState: () => {},
	shouldDisableViewChangerButtons: false,
	customDisabledViewChangerButtonsMessage: '',
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setShouldDisableViewChangerButtons: () => {},
	isNestedModal: false,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setIsNestedModal: () => {},
	displayMinimizableButtons: true,
});

type ProviderProps = {
	initialView?: MinimizableModalView;
	children: ReactNode;
	view?: MinimizableModalView;
	onClose?: MinimizableModalProps['onClose'];
	onViewChange?: (newView: MinimizableModalView) => void;
	displayMinimizableButtons?: boolean;
};

export const MinimizableModalProvider = ({
	initialView = 'modal',
	view,
	onViewChange,
	onClose,
	children,
	displayMinimizableButtons = true,
}: ProviderProps) => {
	/**
	 * When view is passed in as a prop, this modal is controlled
	 * When it is not passed in, _view is used to handle the view changes when the modal is uncontrolled
	 * actualView is the view that we are displaying
	 * previousView is the previously displayed view (so that minimized knows what view to expand to)
	 */
	const [_view, _setView] = useState<MinimizableModalView>(view || initialView);
	const actualView: MinimizableModalView = useMemo(() => view || _view, [view, _view]);
	const previousView = usePrevious<MinimizableModalView>(actualView, ['minimized']);
	const [modalState, setModalState] = useState<TransitionStateEnum>(TransitionState.EXITED);
	const [miniState, setMiniState] = useState<TransitionStateEnum>(TransitionState.EXITED);
	const [minimizedState, setMinimizedState] = useState<TransitionStateEnum>(TransitionState.EXITED);
	const [fullscreenState, setFullscreenState] = useState<TransitionStateEnum>(
		TransitionState.EXITED,
	);
	const [_shouldDisableViewChangerButtons, _setShouldDisableViewChangerButtons] =
		useState<boolean>(false);
	const [_customDisabledViewChangerButtonsMessage, _setCustomDisabledViewChangerButtonsMessage] =
		useState<string>('');
	const [isNestedModal, setIsNestedModal] = useState<boolean>(false);

	const transitionByKeyboardFrom = useRef<MinimizableModalView | null>(null);
	const shouldFocusChanger = useRef(false);

	const currentTransitionState = useMemo(
		() => ({
			modal: modalState,
			mini: miniState,
			minimized: minimizedState,
			fullscreen: fullscreenState,
		}),

		[modalState, miniState, minimizedState, fullscreenState],
	);

	/**
	 * Will always return true if OS reduced motion is turned on since all usages of isTransitionInState
	 * are checking for a transition to be done before doing something
	 * With reduced motion its easiest to assume that the transition is always done since there are no transitions
	 */
	const isTransitionInState = useCallback(
		(
			viewsTransitioning: MinimizableModalView[],
			state: TransitionStateEnum,
			fallbackForReducedMotion = true,
		) => {
			if (isReducedMotion()) {
				return fallbackForReducedMotion;
			}
			return viewsTransitioning.every(
				(viewTransitioning) => currentTransitionState[viewTransitioning] === state,
			);
		},
		[currentTransitionState],
	);

	const setTransitionState = useCallback(
		(viewTransitioning: MinimizableModalView, state: TransitionStateEnum) => {
			if (viewTransitioning === 'modal') setModalState(state);
			if (viewTransitioning === 'mini') setMiniState(state);
			if (viewTransitioning === 'minimized') setMinimizedState(state);
			if (viewTransitioning === 'fullscreen') setFullscreenState(state);
		},
		[],
	);

	const setShouldFocusChanger = (hasTransitioned: boolean) => {
		shouldFocusChanger.current = hasTransitioned;
	};

	const setView = useCallback(
		(newView: MinimizableModalView, force = false) => {
			if (_shouldDisableViewChangerButtons && !force) {
				return;
			}
			if (view) {
				if (onViewChange) {
					onViewChange(newView);
				}
				return;
			}
			_setView(newView);
		},
		[_shouldDisableViewChangerButtons, onViewChange, view],
	);

	const unminimizeModal = useCallback(
		(fallbackView: MinimizableModalView) => {
			// For the case where the user controls the view of the minimizable modal, and starts it as 'minimized'
			if (previousView === 'minimized') {
				setView(fallbackView);
				return;
			}
			setView(previousView);
		},
		[setView, previousView],
	);

	const setTransitionByKeyboardFrom = useCallback((keyPressedFrom: MinimizableModalView | null) => {
		transitionByKeyboardFrom.current = keyPressedFrom;
	}, []);

	const setShouldDisableViewChangerButtons = useCallback(
		(shouldDisableViewChangerButtons: boolean, customDisabledViewChangerButtonsMessage = '') => {
			_setShouldDisableViewChangerButtons(shouldDisableViewChangerButtons);
			_setCustomDisabledViewChangerButtonsMessage(customDisabledViewChangerButtonsMessage);
		},
		[],
	);

	const context = useMemo(
		() => ({
			shouldFocusChanger: shouldFocusChanger.current,
			view: actualView,
			setView,
			previousView,
			onClose,
			unminimizeModal,
			transitionByKeyboardFrom: transitionByKeyboardFrom.current,
			setTransitionByKeyboardFrom,
			setShouldFocusChanger,
			isTransitionInState,
			setTransitionState,
			shouldDisableViewChangerButtons: _shouldDisableViewChangerButtons,
			customDisabledViewChangerButtonsMessage: _customDisabledViewChangerButtonsMessage,
			setShouldDisableViewChangerButtons,
			isNestedModal,
			setIsNestedModal,
			displayMinimizableButtons,
		}),
		[
			_customDisabledViewChangerButtonsMessage,
			_shouldDisableViewChangerButtons,
			actualView,
			isNestedModal,
			isTransitionInState,
			onClose,
			previousView,
			setShouldDisableViewChangerButtons,
			setTransitionByKeyboardFrom,
			setTransitionState,
			setView,
			unminimizeModal,
			displayMinimizableButtons,
		],
	);

	return (
		<MinimizableModalContext.Provider value={context}>{children}</MinimizableModalContext.Provider>
	);
};

export const useMinimizableModal = () => {
	const context = useContext<Context>(MinimizableModalContext);
	if (context === undefined) {
		throw new Error('useMinimizableModal must be used within a MinimizableModalProvider');
	}
	return context;
};

export { MinimizableModalContext };
